import { apiDelete, apiGet, apiPost, apiPut } from './api';

export type SubscriptionPlanName = 'Monthly' | 'Quarterly' | 'Yearly' | 'Trial' | 'Test' | 'Teacher';
export type PaymentGatewayName = 'polar' | 'paypal';
export type PlanLocaleCode = 'en' | 'fr';

export interface PlanLocaleContent {
  displayName: string;
  description: string;
  features: string;
}

export interface PlanLocalesForm {
  en: PlanLocaleContent;
  fr: PlanLocaleContent;
}

export interface AdminSubscriptionPlan {
  _id: string;
  name: SubscriptionPlanName;
  displayName: string;
  description: string;
  price: number;
  currency: string;
  durationDays: number;
  billingCycle: string;
  isRecurring: boolean;
  features: string[];
  isActive: boolean;
  isPopular: boolean;
  discountPercentage: number;
  sortOrder: number;

  polarProductId?: string;
  tags: string[];
  allowedGateways: PaymentGatewayName[];
  locales?: {
    en: { displayName: string; description: string; features: string[] };
    fr: { displayName: string; description: string; features: string[] };
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface SubscriptionPlanFormData {
  name: SubscriptionPlanName;
  locales: PlanLocalesForm;
  price: number;
  currency: string;
  durationDays: number;
  billingCycle: string;
  isRecurring: boolean;
  isActive: boolean;
  isPopular: boolean;
  discountPercentage: number;
  sortOrder: number;

  polarProductId: string;
  tags: string;
  allowedGateways: PaymentGatewayName[];
}

export interface SubscriptionPlanOptions {
  planNames: SubscriptionPlanName[];
  billingCycles: string[];
  currencies: string[];
  durationDays: number[];
  gateways: PaymentGatewayName[];
  locales: PlanLocaleCode[];
}

const PLAN_LOCALE_PRESETS: Record<SubscriptionPlanName, PlanLocalesForm> = {
  Monthly: {
    en: { displayName: 'Monthly Plan', description: '', features: '' },
    fr: { displayName: 'Abonnement mensuel', description: '', features: '' },
  },
  Quarterly: {
    en: { displayName: 'Quarterly Plan', description: '', features: '' },
    fr: { displayName: 'Abonnement trimestriel', description: '', features: '' },
  },
  Yearly: {
    en: { displayName: 'Yearly Plan', description: '', features: '' },
    fr: { displayName: 'Abonnement annuel', description: '', features: '' },
  },
  Trial: {
    en: { displayName: 'Trial Access', description: 'Complimentary access', features: 'Platform access' },
    fr: { displayName: 'Accès essai', description: 'Accès gratuit', features: 'Accès à la plateforme' },
  },
  Teacher: {
    en: {
      displayName: 'Teacher Access',
      description: 'Complimentary access for teachers (admin-managed)',
      features: 'Full platform access',
    },
    fr: {
      displayName: 'Accès professeur',
      description: 'Accès gratuit pour les professeurs (géré par admin)',
      features: 'Accès complet à la plateforme',
    },
  },
  Test: {
    en: {
      displayName: 'Production test (1 day)',
      description: 'Short-lived paid access for checkout testing',
      features: 'Platform access',
    },
    fr: {
      displayName: 'Test production (1 jour)',
      description: 'Accès payant court pour tester le paiement',
      features: 'Accès à la plateforme',
    },
  },
};

export type SubscriptionPlanFormValidationKey =
  | 'displayNameRequired'
  | 'polarProductIdRequired'
  | 'gatewayRequired';

export function validateSubscriptionPlanForm(
  form: SubscriptionPlanFormData,
): SubscriptionPlanFormValidationKey | null {
  if (!form.locales.en.displayName.trim()) {
    return 'displayNameRequired';
  }
  if (form.name !== 'Trial' && form.name !== 'Teacher') {
    if (!form.polarProductId.trim()) {
      return 'polarProductIdRequired';
    }
    if (form.allowedGateways.length === 0) {
      return 'gatewayRequired';
    }
  }
  return null;
}

export async function getSubscriptionPlanOptions() {
  return apiGet<{ success: boolean; options: SubscriptionPlanOptions }>(
    '/api/v1/admin/subscription-plans/options',
  );
}

export async function listSubscriptionPlans() {
  return apiGet<{ success: boolean; activeGateway: string; plans: AdminSubscriptionPlan[] }>(
    '/api/v1/admin/subscription-plans',
  );
}

export async function createSubscriptionPlan(body: Record<string, unknown>) {
  return apiPost<{ success: boolean; plan: AdminSubscriptionPlan }>(
    '/api/v1/admin/subscription-plans',
    body,
  );
}

export async function updateSubscriptionPlan(planId: string, body: Record<string, unknown>) {
  return apiPut<{ success: boolean; plan: AdminSubscriptionPlan }>(
    `/api/v1/admin/subscription-plans/${planId}`,
    body,
  );
}

export async function deleteSubscriptionPlan(planId: string) {
  return apiDelete<{ success: boolean; message?: string }>(
    `/api/v1/admin/subscription-plans/${planId}`,
  );
}

export interface SubscriptionSettings {
  registrationTrialEnabled: boolean;
  registrationTrialDays: number;
  lastChangedAt?: string;
  notes?: string;
}

export async function getSubscriptionSettings() {
  return apiGet<{ success: boolean; settings: SubscriptionSettings }>(
    '/api/v1/admin/subscription-plans/settings',
  );
}

export async function updateSubscriptionSettings(body: Partial<SubscriptionSettings>) {
  return apiPut<{ success: boolean; settings: SubscriptionSettings }>(
    '/api/v1/admin/subscription-plans/settings',
    body,
  );
}

function localeBlockToForm(
  block?: { displayName?: string; description?: string; features?: string[] },
): PlanLocaleContent {
  return {
    displayName: block?.displayName || '',
    description: block?.description || '',
    features: (block?.features || []).join('\n'),
  };
}

export function planToForm(plan: AdminSubscriptionPlan): SubscriptionPlanFormData {
  return {
    name: plan.name,
    locales: {
      en: localeBlockToForm(plan.locales?.en || {
        displayName: plan.displayName,
        description: plan.description,
        features: plan.features,
      }),
      fr: localeBlockToForm(plan.locales?.fr),
    },
    price: plan.price,
    currency: plan.currency,
    durationDays: plan.durationDays,
    billingCycle: plan.billingCycle,
    isRecurring: plan.isRecurring,
    isActive: plan.isActive,
    isPopular: plan.isPopular,
    discountPercentage: plan.discountPercentage || 0,
    sortOrder: plan.sortOrder || 0,
    polarProductId: plan.polarProductId || '',
    tags: (plan.tags || []).join(', '),
    allowedGateways: plan.allowedGateways || [],
  };
}

export function defaultPlanForm(name: SubscriptionPlanName = 'Monthly'): SubscriptionPlanFormData {
  const presets: Record<SubscriptionPlanName, Partial<SubscriptionPlanFormData>> = {
    Monthly: { durationDays: 30, billingCycle: 'monthly', isRecurring: true, allowedGateways: ['polar'] },
    Quarterly: { durationDays: 90, billingCycle: 'quarterly', isRecurring: true, allowedGateways: ['polar'] },
    Yearly: { durationDays: 365, billingCycle: 'yearly', isRecurring: false, allowedGateways: ['polar', 'paypal'] },
    Trial: { durationDays: 30, billingCycle: 'monthly', isRecurring: false, price: 0, allowedGateways: [] },
    Teacher: { durationDays: 365, billingCycle: 'yearly', isRecurring: false, price: 0, allowedGateways: [], tags: 'teacher, teacher-role', sortOrder: -2 },
    Test: {
      durationDays: 1,
      billingCycle: 'daily',
      isRecurring: false,
      price: 1,
      allowedGateways: ['polar'],
      sortOrder: 99,
      tags: 'test',
    },
  };
  return {
    name,
    locales: PLAN_LOCALE_PRESETS[name],
    price: 0,
    currency: 'EUR',
    durationDays: 30,
    billingCycle: 'monthly',
    isRecurring: false,
    isActive: true,
    isPopular: false,
    discountPercentage: 0,
    sortOrder: 0,
    polarProductId: '',
    tags: '',
    allowedGateways: ['polar'],
    ...presets[name],
  };
}

export function formToPayload(form: SubscriptionPlanFormData) {
  const toFeatures = (text: string) =>
    text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

  return {
    name: form.name,
    price: form.price,
    currency: form.currency,
    durationDays: form.durationDays,
    billingCycle: form.billingCycle,
    isRecurring: form.isRecurring,
    isActive: form.isActive,
    isPopular: form.isPopular,
    discountPercentage: form.discountPercentage,
    sortOrder: form.sortOrder,
    polarProductId: form.polarProductId,
    tags: form.tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean),
    allowedGateways: form.name === 'Trial' || form.name === 'Teacher' ? [] : form.allowedGateways,
    locales: {
      en: {
        displayName: form.locales.en.displayName,
        description: form.locales.en.description,
        features: toFeatures(form.locales.en.features),
      },
      fr: {
        displayName: form.locales.fr.displayName,
        description: form.locales.fr.description,
        features: toFeatures(form.locales.fr.features),
      },
    },
  };
}

export function getPlanDisplayLabel(plan: AdminSubscriptionPlan, locale: PlanLocaleCode = 'en') {
  return plan.locales?.[locale]?.displayName || plan.displayName || plan.name;
}
